import { Component, OnInit, OnDestroy, ViewChild, ViewChildren, QueryList, ElementRef } from '@angular/core';
import { ApiService } from '../api.service';
import { ActivatedRoute, Router } from '@angular/router';
import { staticInOut } from '../router.animations';
import {
  trigger,
  state,
  style,
  animate,
  transition
} from '@angular/animations';
import { appConfig } from './../app.config';
import { Meta } from '@angular/platform-browser';
import * as imagesLoaded from 'imagesloaded';
import { compact, isUndefined, map, capitalize, join, find, forEach, forEachRight, drop, last, sortBy, reverse, assign } from 'lodash';
import { LayoutService } from '../layout.service';
import { NavigationService } from '../navigation.service';

@Component({
  selector: 'app-table-view',
  templateUrl: './table-view.component.html',
  styleUrls: ['./table-view.component.scss'],
  animations: [
    staticInOut,
    trigger('fadeInOut', [
      state('hidden', style({
        opacity: '0'
      })),
      state('visible',   style({
        opacity: '1'
      })),
      transition('hidden => visible', animate('250ms ease-in')),
      transition('visible => hidden', animate('250ms ease-out'))
    ])
  ],
  host: {
    '(window:resize)': 'onResize($event)',
    '[@staticInOut]': ''
  }
})
export class TableViewComponent implements OnInit, OnDestroy {
  @ViewChild('scrollWindow') scrollWindowRef: ElementRef;
  @ViewChild('table') tableRef: ElementRef;
  @ViewChildren('tableColumn') tableColumnRef: QueryList<ElementRef>;
  @ViewChildren('cell') cellRef: QueryList<ElementRef>;
  @ViewChild('thumb') thumbRef: ElementRef;
  public imageSrcBase = appConfig.imageUrl;
  scrollWindowEl;
  tableEl;
  containerEl;
  rows: any[];
  sortByKey: string;
  sortAsc: boolean;
  hoverItem: any = null;
  columns: any;
  isInitialised: boolean = false;
  hasThumb: boolean = false;
  columnTitle: any = {
    title: "project",
    date: "year",
    country: "country",
    parent_id: "category",
    project_type: "type",
    materials: "material"
  };

  get scrollTop() {
    return typeof this.scrollWindowEl === 'undefined' ? 0 : this.scrollWindowEl.scrollTop || document.body.scrollTop;
  }

  constructor(
    private NavigationService: NavigationService,
    private ApiService: ApiService,
    private route: ActivatedRoute,
    private router: Router,
    private elRef: ElementRef,
    private metaService: Meta,
    private LayoutService: LayoutService
  ) {
  }

  getApiData() {
    return this.ApiService.getData(`homepage_settings`)
      .then((homepageSettings) => {
        let settings = homepageSettings['homepage_settings'][0];
        return this.ApiService.getData(`index?orderBy=${settings.default_index_order_col}&sort=${settings.default_index_order_by}&index_cols=parent_id,id,slug,thumbnail_id,is_disabled,${settings.index_cols}`).then((indexData) => {
          return assign({}, {
            rows: indexData['index']
          }, settings);
        });
      });
  }

  initData(apiData) {
    let sortByParam = this.route.snapshot.queryParams['sortBy'];
    let sortParam = this.route.snapshot.queryParams['sort'];
    let colNames = apiData.index_cols.split(',');
    let apiOrder = apiData.index_cols[0];
    if (apiData.default_index_order_col !== '') {
      apiOrder = apiData.default_index_order_col;
    }

    this.sortByKey = isUndefined(sortByParam) ? apiOrder : sortByParam.toLowerCase();
    this.sortAsc = isUndefined(sortParam) ? apiData.default_index_order_by === 'asc' : sortParam.toLowerCase() === 'asc' ? true : false;
    this.columns = map(colNames, (columnName: any, index: number) => {
      return {
        id: index,
        title: this.columnTitle[columnName],
        name: columnName,
        isVisible: true
      }
    });

    const rows = compact(map(apiData.rows, (item: any) => {
      if (item.is_disabled === 1) {
        return null;
      }

      item.dateString = new Date(item.date).getFullYear();
      item.materialsString = capitalize(join(map(item.materials, 'text'), ', '));
      let category: any = find(window['categories'], { id: item.parent_id });
      item.category = isUndefined(category) ? '' : category['title'];

      let url = isUndefined(category) ? '' : '/' + category['slug'];
      url += '/all/' + item.slug;
      item.url = (url).toLowerCase();

      return item;
    }));

    this.rows = rows;

    this.sortTable(this.sortByKey, this.sortAsc);

    this.metaService.updateTag({ name: 'keywords', content: 'Danny Lane' });
    this.metaService.updateTag({ name: 'description', content: 'Danny Lane' });
  }

  ngOnInit() {
    this.getApiData().then(apiData => {
      this.initData(apiData);

      this.preloadItemThumbs();

      setTimeout(() => {
        this.scrollWindowEl = this.scrollWindowRef.nativeElement;
        this.tableEl = this.tableRef.nativeElement;
        this.containerEl = this.tableEl.parentNode;
        this.getColWidths();
        
        setTimeout(() => {
          this.setLayout();
          this.isInitialised = true;
        
          this.getColPositionLeft();

          if (this.NavigationService.isBack) {
            setTimeout(() => {
              this.scrollWindowEl.scrollTop = this.LayoutService.getGridScrollPosition('index-page');
              document.body.scrollTop = this.LayoutService.getGridScrollPosition('index-page');
            });
          }
        });
      }, 400);
    });
  }
  
  trackItem(index, item) {
    return item ? item.id : undefined;
  }

  getColPositionLeft() {
    this._getColPositionLeft();
    setTimeout(() => {
      this._getColPositionLeft();
    }, 200);
  }

  _getColPositionLeft() {
    forEach(this.tableColumnRef.toArray(), (columnRef) => {
      let colId = columnRef.nativeElement.id;
      let left = columnRef.nativeElement.getBoundingClientRect().left;
      let column: any = find(this.columns, { id: parseInt(colId) });
      column.left = left;
    });
  }

  getColWidths() {
    forEach(this.tableColumnRef.toArray(), (columnRef) => {
      let colId = columnRef.nativeElement.id;
      let width = columnRef.nativeElement.clientWidth;
      let column: any = find(this.columns, { id: parseInt(colId) });

      column.width = width;
    });
  }

  fitTable() {
    let cumulativeWidth: number = this.columns[0].width + this.columns[1].width;
    
    forEachRight(drop(this.columns, 2), (column: any) => {
      cumulativeWidth += column.width;
      if (cumulativeWidth > this.tableEl.clientWidth) {
        column.isVisible = false;
      } else {
        column.isVisible = true;
      }
    });
  }

  setLayout() {
    this.fitTable();
    
    if (!window['Modernizr'].touchevents && (<any>last(this.columns)).isVisible) {
      this.hasThumb = true;
    }
  }

  onResize(event) {
    if (this.isInitialised) {
      this.setLayout();
    }
    this.getColPositionLeft();
  }

  sortTable(key, sortAsc) {
    this.sortAsc = isUndefined(sortAsc) ? !(this.sortByKey === key && this.sortAsc) : sortAsc;
    let actualSortKey = key;
    if (key === 'materials') {
      actualSortKey = 'materialsString';
    } else if (key === 'date') {
      actualSortKey = 'dateString';
    } else if (key === 'parent_id') {
      actualSortKey = 'category';
    }
    let data = sortBy(this.rows, [actualSortKey]);
    if (!this.sortAsc) {
      data = reverse(data);
    }
    this.setRows(data, { sortAsc: this.sortAsc, sortKey: key });
    this.sortByKey = key;

    this.router.navigate([], {
      queryParams: {
        'sortBy': key,
        'sort': this.sortAsc ? 'asc' : 'desc'
      },
      replaceUrl: true
    });
  }

  setRows(rows, params) {
    this.rows = rows;
    localStorage.setItem('index', JSON.stringify(rows.map(item => ({page_id: item.id, url: item.url}))));
    localStorage.setItem('indexparams', JSON.stringify(params));
  }

  itemSelect(item) {
    setTimeout(() => {
      const showThumb = ((this.hoverItem && this.hoverItem.id !== item.id) || !this.hoverItem) && this.hasThumb;
      this.hoverItem = item;
      if (showThumb) {
        this.showThumbnail(item);
      }
    });
  }

  itemDeselect() {
    this.hoverItem = null;
  }

  tableLeave() {
    this.itemDeselect();
  }

  onScroll() {
    this.itemDeselect();
  }

  onCellClick() {
    this.LayoutService.saveGridScrollPosition('index-page', this.scrollTop);
  }

  preloadItemThumbs() {
    forEach(this.rows, (item) => {
      let img = new Image();
      item.image = isUndefined(item.thumbnail) ? {} : item.thumbnail[0];
    });
  }

  ngOnDestroy() {
    this.LayoutService.saveGridScrollPosition('index-page', this.scrollTop);
  }

  showThumbnail(item) {
    item.image.isHidden = true;
    
    let cellId = `cell_${(<any>last(this.columns)).id}_${item.id}`;
    let cellRef = find(this.cellRef.toArray(), (elRef) => elRef.nativeElement.id === cellId);
    if (isUndefined(cellRef)) {
      return;
    }
    let cellInner = cellRef.nativeElement.getElementsByClassName('cell_inner')[0];
    let bounding = cellInner.getBoundingClientRect();
    
    item.image.left = bounding.left;
    item.image.top = bounding.top - this.elRef.nativeElement.getBoundingClientRect().top + cellInner.clientHeight;
    item.image.width = cellInner.clientWidth;

    setTimeout(() => {
      if (typeof this.thumbRef === 'undefined') {
        return;
      }

      let thumbEl = this.thumbRef.nativeElement;

      imagesLoaded(thumbEl, () => {
        let topWhenBelow = item.image.top;
        let topWhenAbove = item.image.top - thumbEl.clientHeight - cellInner.clientHeight;

        let boundingBoxWhenAbove = thumbEl.getBoundingClientRect();
        let offscreenWhenBelow = window.innerHeight - boundingBoxWhenAbove.bottom;

        // Move above row
        if (offscreenWhenBelow < 0) {
          item.image.top = topWhenAbove;

          setTimeout(() => {
            let boundingBoxWhenBelow = thumbEl.getBoundingClientRect();
            let offscreenWhenAbove = boundingBoxWhenBelow.top;

            // Move back below
            if (offscreenWhenAbove < offscreenWhenBelow) {
              item.image.top = topWhenBelow;
            }

            item.image.isHidden = false;
          });
        } else {
          item.image.isHidden = false;
        }
      });
    });
  }
}



// WEBPACK FOOTER //
// ./src/app/table-view/table-view.component.ts